import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setPdfZoom, setCurrentPage } from "../../../../../redux/pdfSlice";
import { useGetTheme } from "../../../../../hooks";
import { useIntl } from "react-intl";
import clsx from "clsx";
import { makeStyles } from "@mui/styles";
import { scrollPageIntoView } from "../../../../reader/pdf/utils";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Popover,
  Paper,
  TextField
} from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import AddIcon from "@mui/icons-material/Add";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";

const useStyles = makeStyles((theme) => ({
  barIcon: {
    height: "24px",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    }
  },
  isCompatReaderActionButton: {
    height: "30px",
    width: "30px"
  },
  isCompactContainer: {
    marginInlineEnd: theme.spacing(1)
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  zoomDisplay: {
    minWidth: "60px",
    textAlign: "center"
  },
  resetButton: {
    minWidth: "unset",
    borderRadius: "4px",
    padding: theme.spacing(0.5, 1.5),
    textTransform: "none",
    color: theme.palette.primary.light,
    border: `1px solid ${theme.palette.primary.light}`,
    fontWeight: 400,
    fontSize: "0.875rem",
    "&:hover": {
      backgroundColor: theme.palette.grey[200]
    }
  },
  zoomControls: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },
  pageControls: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  pageInput: {
    width: "50px",
    "& input": {
      textAlign: "center",
      padding: theme.spacing(0.5)
    }
  },
  pageDisplay: {
    display: "flex",
    alignItems: "center",
    minWidth: "80px",
    justifyContent: "center"
  }
}));

const PdfActionBar = ({ contentWidth, showPagesControls = false }) => {
  const classes = useStyles();
  const theme = useGetTheme();
  const intl = useIntl();
  const dispatch = useDispatch();
  const zoom = useSelector((state) => state.pdf.zoom);
  const totalPages = useSelector((state) => state.pdf.totalPages);
  const currentPage = useSelector((state) => state.pdf.currentPage);

  const [isCompact, setIsCompact] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const popoverId = open ? "zoom-popover" : undefined;
  const [pageInputValue, setPageInputValue] = useState(currentPage.toString());

  // Check if the content panel is narrow and switch to compact mode
  useEffect(() => {
    setIsCompact(contentWidth < 660);
  }, [contentWidth]);

  // Update page input value when current page changes
  useEffect(() => {
    setPageInputValue(currentPage.toString());
  }, [currentPage]);

  // Handlers for zoom actions
  const handleZoomIn = () => {
    dispatch(setPdfZoom(zoom + 0.1));
  };

  const handleZoomOut = () => {
    if (zoom > 0.1) {
      dispatch(setPdfZoom(zoom - 0.1));
    }
  };
  const handleResetZoom = () => {
    dispatch(setPdfZoom(1));
  };

  const handleOpenPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };
  // Handlers for page navigation
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      dispatch(setCurrentPage(nextPage));
      scrollPageIntoView(nextPage, { behavior: "smooth", block: "start" });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      dispatch(setCurrentPage(prevPage));
      scrollPageIntoView(prevPage, { behavior: "smooth", block: "start" });
    }
  };

  const handlePageInputChange = (event) => {
    setPageInputValue(event.target.value);
  };
  const handlePageInputBlur = () => {
    let newPage = parseInt(pageInputValue, 10);
    if (isNaN(newPage) || newPage < 1) {
      newPage = 1;
    } else if (newPage > totalPages) {
      newPage = totalPages;
    }
    setPageInputValue(newPage.toString());
    dispatch(setCurrentPage(newPage));
    scrollPageIntoView(newPage, { behavior: "smooth", block: "start" });
  };

  const handlePageInputKeyDown = (event) => {
    if (event.key === "Enter") {
      handlePageInputBlur();
    }
  };

  // Format zoom as percentage
  const zoomPercentage = `${Math.round(zoom * 100)}%`;

  // Component for the container
  const containerClass = clsx(
    classes.buttonsContainer,
    isCompact && classes.isCompactContainer
  );
  return (
    <Box
      sx={{ paddingInlineEnd: theme.spacing(0.5) }}
      className={containerClass}>
      {showPagesControls && (
        <Box className={classes.pageControls}>
          <IconButton
            size="medium"
            onClick={handlePrevPage}
            disabled={currentPage <= 1}
            aria-label={intl.formatMessage({
              id: "pdf.previousPage",
              defaultMessage: "Previous page"
            })}
            title={intl.formatMessage({
              id: "pdf.previousPage",
              defaultMessage: "Previous page"
            })}>
            <NavigateBeforeIcon fontSize="medium" />
          </IconButton>

          <Box className={classes.pageDisplay}>
            <TextField
              className={classes.pageInput}
              value={pageInputValue}
              onChange={handlePageInputChange}
              onBlur={handlePageInputBlur}
              onKeyDown={handlePageInputKeyDown}
              size="small"
              variant="outlined"
              inputProps={{
                "aria-label": intl.formatMessage({
                  id: "pdf.pageNumber",
                  defaultMessage: "Page number"
                })
              }}
            />
            <Typography variant="body2" sx={{ mx: 0.5 }}>
              /
            </Typography>
            <Typography variant="body2">{totalPages}</Typography>
          </Box>

          <IconButton
            size="medium"
            onClick={handleNextPage}
            disabled={currentPage >= totalPages}
            aria-label={intl.formatMessage({
              id: "pdf.nextPage",
              defaultMessage: "Next page"
            })}
            title={intl.formatMessage({
              id: "pdf.nextPage",
              defaultMessage: "Next page"
            })}>
            <NavigateNextIcon fontSize="medium" />
          </IconButton>
        </Box>
      )}
      <Box>
        <IconButton
          size="medium"
          onClick={handleOpenPopover}
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={intl.formatMessage({
            id: "pdf.openZoomControls",
            defaultMessage: "Open zoom controls"
          })}
          title={intl.formatMessage({
            id: "pdf.openZoomControls",
            defaultMessage: "Open zoom controls"
          })}>
          <ZoomInIcon fontSize="medium" />
        </IconButton>
      </Box>{" "}
      {/* Popover with zoom controls */}
      <Popover
        open={open}
        id={popoverId}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center"
        }}>
        <Paper sx={{ p: 2 }}>
          <Box className={classes.zoomControls}>
            <IconButton
              size="medium"
              onClick={handleZoomOut}
              disabled={zoom <= 0.1}
              aria-label={intl.formatMessage({
                id: "pdf.zoomOut",
                defaultMessage: "Zoom out"
              })}
              title={intl.formatMessage({
                id: "pdf.zoomOut",
                defaultMessage: "Zoom out"
              })}>
              <RemoveIcon fontSize="medium" />
            </IconButton>

            <Typography
              variant="body2"
              className={classes.zoomDisplay}
              aria-live="polite"
              role="status"
              aria-label={intl.formatMessage(
                {
                  id: "pdf.currentZoom",
                  defaultMessage: "Current zoom level {zoomLevel}"
                },
                { zoomLevel: zoomPercentage }
              )}>
              {zoomPercentage}
            </Typography>

            <IconButton
              size="medium"
              onClick={handleZoomIn}
              aria-label={intl.formatMessage({
                id: "pdf.zoomIn",
                defaultMessage: "Zoom in"
              })}
              title={intl.formatMessage({
                id: "pdf.zoomIn",
                defaultMessage: "Zoom in"
              })}>
              <AddIcon fontSize="medium" />
            </IconButton>

            <Button
              variant="outlined"
              size="small"
              onClick={handleResetZoom}
              className={classes.resetButton}
              aria-label={intl.formatMessage({
                id: "pdf.resetZoom",
                defaultMessage: "Reset zoom to 100%"
              })}
              title={intl.formatMessage({
                id: "pdf.resetZoom",
                defaultMessage: "Reset zoom to 100%"
              })}>
              {intl.formatMessage({ id: "pdf.reset", defaultMessage: "Reset" })}
            </Button>
          </Box>
        </Paper>
      </Popover>
    </Box>
  );
};

export default PdfActionBar;
